import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../context/LanguageContext';
import { useGuest } from '../context/GuestContext';
import SeonaidScratch from './SeonaidScratch';
import '../styles/GuestGate.css';

const GuestGate = ({ onAuthenticated }) => {
  const { t } = useTranslation();
  const { authenticate } = useGuest();
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [rejected, setRejected] = useState(false);
  const [shaking, setShaking] = useState(false);

  // Anthony medal state
  const [anthonyGuest, setAnthonyGuest] = useState(null);
  const [medals, setMedals] = useState('');
  const [medalResponse, setMedalResponse] = useState(null);

  // Bride/Groom password state
  const [coupleGuest, setCoupleGuest] = useState(null);
  const [couplePassword, setCouplePassword] = useState('');
  const [couplePasswordWrong, setCouplePasswordWrong] = useState(false);

  // Seonaid scratch state
  const [seonaidGuest, setSeonaidGuest] = useState(null);

  // Mátyás dodge state
  const [matyasGuest, setMatyasGuest] = useState(null);
  const [dodgeCount, setDodgeCount] = useState(0);
  const [buttonPos, setButtonPos] = useState(null);

  const getRandomPos = useCallback(() => {
    const maxX = window.innerWidth - 200;
    const maxY = window.innerHeight - 60;
    return {
      x: Math.max(20, Math.random() * maxX),
      y: Math.max(20, Math.random() * maxY),
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!firstName.trim() || !surname.trim()) return;

    const found = authenticate(firstName, surname);
    if (found) {
      if (found.special === 'amiker' || found.special === 'gergely') {
        setCoupleGuest(found);
      } else if (found.special === 'anthony') {
        setAnthonyGuest(found);
      } else if (found.special === 'seonaid') {
        setSeonaidGuest(found);
      } else if (found.special === 'matyas') {
        setMatyasGuest(found);
      } else {
        onAuthenticated(found);
      }
    } else {
      setShaking(true);
      setTimeout(() => {
        setShaking(false);
        setRejected(true);
      }, 500);
    }
  };

  const handleMedalSubmit = (e) => {
    e.preventDefault();
    const num = parseInt(medals, 10);
    if (isNaN(num) || medals.trim() === '') return;
    if (num <= 50) setMedalResponse('Only?');
    else if (num <= 90) setMedalResponse('Hmmm...');
    else setMedalResponse('So close!!');
  };

  const handleMedalContinue = () => {
    onAuthenticated(anthonyGuest);
  };

  const handleMatyasDodge = () => {
    if (dodgeCount < 3) {
      setDodgeCount(dodgeCount + 1);
      setButtonPos(getRandomPos());
    } else {
      onAuthenticated(matyasGuest);
    }
  };

  const handleTryAgain = () => {
    setRejected(false);
    setFirstName('');
    setSurname('');
  };

  // Bride/Groom password screen
  if (coupleGuest) {
    const handleCouplePassword = (e) => {
      e.preventDefault();
      if (couplePassword.trim().toLowerCase() === 'sugaboo') {
        onAuthenticated(coupleGuest);
      } else {
        setCouplePasswordWrong(true);
        setTimeout(() => setCouplePasswordWrong(false), 1500);
      }
    };

    return (
      <motion.div
        className="guest-gate"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="gate-card"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{
            scale: 1,
            opacity: 1,
            y: 0,
            x: couplePasswordWrong ? [0, -10, 10, -10, 10, 0] : 0,
          }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="gate-title">One More Thing...</h1>
          <p className="gate-prompt">What's the secret word?</p>
          <form onSubmit={handleCouplePassword} className="gate-form">
            <input
              type="password"
              className="gate-input"
              placeholder="Secret word"
              value={couplePassword}
              onChange={(e) => setCouplePassword(e.target.value)}
              autoComplete="off"
            />
            <button type="submit" className="gate-button">
              {t('gate.continue')}
            </button>
          </form>
          {couplePasswordWrong && (
            <motion.p
              className="gate-rejected-message"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ marginTop: '1rem' }}
            >
              Nope, try again!
            </motion.p>
          )}
        </motion.div>
      </motion.div>
    );
  }

  // Seonaid scratch screen
  if (seonaidGuest) {
    return <SeonaidScratch onComplete={() => onAuthenticated(seonaidGuest)} />;
  }

  // Anthony medal screen
  if (anthonyGuest) {
    return (
      <motion.div
        className="guest-gate"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="gate-card"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="gate-title">One More Thing...</h1>
          {!medalResponse ? (
            <form onSubmit={handleMedalSubmit} className="gate-form">
              <input
                type="number"
                className="gate-input"
                placeholder="How many Medals?"
                value={medals}
                onChange={(e) => setMedals(e.target.value)}
                min="0"
                inputMode="numeric"
              />
              <button type="submit" className="gate-button">
                Submit
              </button>
            </form>
          ) : (
            <>
              <p className="gate-medal-response">{medalResponse}</p>
              <button className="gate-button" onClick={handleMedalContinue}>
                {t('gate.continue')}
              </button>
            </>
          )}
        </motion.div>
      </motion.div>
    );
  }

  // Mátyás dodge screen
  if (matyasGuest) {
    return (
      <motion.div
        className="guest-gate"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="gate-card"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="gate-title">{t('gate.title')}</h1>
          <p className="gate-prompt">Welcome, {matyasGuest.firstName}!</p>
        </motion.div>

        <motion.button
          className="gate-button matyas-dodge-btn"
          onClick={handleMatyasDodge}
          animate={buttonPos ? { left: buttonPos.x, top: buttonPos.y } : {}}
          transition={{ type: 'spring', damping: 15, stiffness: 300 }}
          style={buttonPos ? { position: 'fixed', zIndex: 1001 } : {}}
        >
          {dodgeCount < 3 ? t('gate.continue') : 'OK FINE, Come In'}
        </motion.button>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="guest-gate"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <AnimatePresence mode="wait">
        {!rejected ? (
          <motion.div
            key="form"
            className="gate-card"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{
              scale: 1,
              opacity: 1,
              y: 0,
              x: shaking ? [0, -10, 10, -10, 10, 0] : 0,
            }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="gate-subtitle">{t('gate.subtitle')}</h2>
            <h1 className="gate-title">{t('gate.title')}</h1>
            <p className="gate-prompt">{t('gate.prompt')}</p>

            <form onSubmit={handleSubmit} className="gate-form">
              <input
                type="text"
                className="gate-input"
                placeholder={t('gate.firstName')}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
              <input
                type="text"
                className="gate-input"
                placeholder={t('gate.surname')}
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                autoComplete="family-name"
              />
              <button type="submit" className="gate-button">
                {t('gate.continue')}
              </button>
            </form>
          </motion.div>
        ) : (
          <motion.div
            key="rejected"
            className="gate-card gate-rejected"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="gate-title">{t('gate.rejectedTitle')}</h1>
            <p className="gate-rejected-message">{t('gate.rejectedMessage')}</p>
            <button className="gate-button" onClick={handleTryAgain}>
              {t('gate.tryAgain')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default GuestGate;
